// Shared Google Drive Picker integration (Block 178).
//
// One entry point — pickGoogleDoc({ fileIds }) — used by BOTH the meeting-transcript
// flow and the project Doc import. It:
//   1. lazy-loads the GAPI Picker library and Google Identity Services (GIS),
//   2. obtains a `drive.file` access token by INCREMENTAL consent (it requests ONLY
//      that scope, so the server-side gmail/calendar grants are never touched and the
//      user is never forced to reconnect),
//   3. opens the Picker — pre-navigated to a known file via DocsView.setFileIds when a
//      fileId is supplied (one click), or unnavigated so the user finds the file (a few),
//   4. resolves { fileId, name, accessToken }, or null if the user cancels.
//
// drive.file is per-file and non-restricted: Google grants Aidey access only to the exact
// files the user picks, which is why it needs no CASA assessment (unlike drive.readonly).

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || null;
const API_KEY   = process.env.REACT_APP_GOOGLE_API_KEY || null; // Picker developer key (Block 178 commit 4: restored)
const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const LOAD_TIMEOUT_MS = 10000;  // non-interactive script loads are bounded, a hang becomes a visible error
const AUTH_TIMEOUT_MS = 120000; // the OAuth step is interactive (a consent screen the user reads and clicks),
                                // so it gets a generous cap — 10s would abort a legitimate consent — while
                                // still bounding a true hang. Re-consent (below) happens inside this budget.

// Reject if `promise` doesn't settle within `ms`. This is the backstop that turns a silent hang
// (Commit 1b's bug) into a real error the caller can show.
function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms); });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// ── Script readiness (Commit 1b fix) ─────────────────────────────────────────
// Readiness is determined by the GLOBAL the script provides — NOT by a load event on the tag.
// The previous bug attached a `load` listener to an already-present tag (injected by
// @react-oauth/google for GIS, or by the calendar/gmail code for api.js); that tag finished
// loading long ago, so the event never fired again and the promise never settled. Here:
//   • if the global is already present → resolve immediately (covers the "foreign tag already
//     loaded" case with no dependence on any event),
//   • else inject the tag only if it's genuinely absent, then POLL for the global until it
//     appears or LOAD_TIMEOUT_MS elapses (works regardless of whether the load event already fired).
function ensureScript(src, isReady, label) {
  if (isReady()) return Promise.resolve();
  if (!document.querySelector(`script[src="${src}"]`)) {
    const el = document.createElement('script');
    el.src = src; el.async = true; el.defer = true;
    document.head.appendChild(el);
  }
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const iv = setInterval(() => {
      if (isReady()) { clearInterval(iv); resolve(); }
      else if (Date.now() - started > LOAD_TIMEOUT_MS) { clearInterval(iv); reject(new Error(`${label} timed out`)); }
    }, 100);
  });
}

let pickerLoaded = null;
function ensurePickerLoaded() {
  if (pickerLoaded) return pickerLoaded;
  pickerLoaded = ensureScript('https://apis.google.com/js/api.js', () => !!window.gapi, 'Google API')
    .then(() => withTimeout(new Promise((resolve, reject) => {
      window.gapi.load('picker', { callback: () => resolve(), onerror: () => reject(new Error('Failed to load the Google Picker')) });
    }), LOAD_TIMEOUT_MS, 'Google Picker'))
    .catch((e) => { pickerLoaded = null; throw e; }); // clear memo so a later attempt can retry
  return pickerLoaded;
}

function ensureGisLoaded() {
  // Not memoised: ensureScript resolves instantly once window.google.accounts.oauth2 exists.
  return ensureScript('https://accounts.google.com/gsi/client', () => !!window.google?.accounts?.oauth2, 'Google sign-in');
}

// ── Incremental drive.file token via GIS ─────────────────────────────────────
// Requests ONLY drive.file. Because it is a distinct token client scoped to that one
// permission, it does not disturb the gmail/calendar grant stored server-side — no
// forced reconnect. Cached in-memory for the session and reused until it errors.
let cachedToken = null;
function requestToken({ forceConsent }) {
  return new Promise((resolve, reject) => {
    if (cachedToken && !forceConsent) { resolve(cachedToken); return; }
    if (!CLIENT_ID) { reject(new Error('Google Drive isn’t configured (missing client id). Contact support.')); return; }
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: DRIVE_FILE_SCOPE, // ← ONLY drive.file, incremental, nothing else re-requested
      callback: (resp) => {
        if (!resp || !resp.access_token) { reject(new Error(resp?.error || 'Google did not return access')); return; }
        // Capture the ACTUALLY-granted scopes — the field that answers, in one line, whether
        // drive.file is present. GIS can silently return a token minted under an OLDER grant that
        // predates drive.file being added to the consent screen; that token 403s the Picker.
        console.log('[drive-picker] granted scope:', resp.scope || '(none returned)');
        if (window.google.accounts.oauth2.hasGrantedAllScopes(resp, DRIVE_FILE_SCOPE)) {
          cachedToken = resp.access_token;
          resolve(resp.access_token);
          return;
        }
        // drive.file is MISSING. If we haven't already forced consent, clear the cache and force the
        // consent screen EXACTLY ONCE (wiring the forceConsent flag). Never a loop.
        if (!forceConsent) {
          console.warn('[drive-picker] token is missing drive.file — clearing cache and re-consenting once');
          cachedToken = null;
          requestToken({ forceConsent: true }).then(resolve, reject);
          return;
        }
        // Re-consent ran and STILL no drive.file → hard stop, truthful error (surfaced by callers).
        const e = new Error('drive.file was not granted');
        e.code = 'drive_scope_denied';
        reject(e);
      },
      // GIS reports WHY it failed via err.type. We copy it onto err.code so callers can single out
      // the pop-up-blocked case ('popup_failed_to_open') and only then show the "allow pop-ups" copy.
      error_callback: (err) => {
        const e = new Error(err?.message || 'Google authorisation was dismissed');
        e.code = err?.type; // 'popup_failed_to_open' | 'popup_closed' | ...
        reject(e);
      },
    });
    // prompt:'consent' forces the consent screen (used for the one re-consent); '' reuses an existing
    // grant when possible. Google still shows per-file consent through the Picker selection itself.
    tokenClient.requestAccessToken({ prompt: forceConsent ? 'consent' : '' });
  });
}

function getDriveFileToken({ forceConsent = false } = {}) {
  // Call requestToken SYNCHRONOUSLY when GIS is already loaded (it's preloaded on open) so the pop-up
  // stays inside the click's user-gesture window; only await the loader if it isn't ready yet. One
  // outer timeout bounds the WHOLE flow (initial request + the single re-consent) so a true hang still
  // surfaces, but a consent screen the user is reading isn't aborted.
  const go = () => (window.google?.accounts?.oauth2
    ? requestToken({ forceConsent })
    : ensureGisLoaded().then(() => requestToken({ forceConsent })));
  return withTimeout(go(), AUTH_TIMEOUT_MS, 'Google authorisation');
}

// ── Open the Picker ──────────────────────────────────────────────────────────
// fileIds (optional): pre-navigate straight to these via DocsView.setFileIds — Google
// then prompts the user to grant drive.file access to exactly those files. When omitted,
// the Picker opens unnavigated and the user browses to the file themselves.
function openPicker({ accessToken, fileIds }) {
  return new Promise((resolve, reject) => {
    try {
      const google = window.google;
      const view = new google.picker.DocsView(google.picker.ViewId.DOCUMENTS)
        .setMimeTypes('application/vnd.google-apps.document');
      // setFileIds pre-navigates; do NOT combine it with setParent/setEnableDrives.
      if (Array.isArray(fileIds) && fileIds.length) view.setFileIds(fileIds.join(','));

      const builder = new google.picker.PickerBuilder()
        // setOrigin tells the Picker which parent origin to postMessage its result back to. Without
        // it the Picker misinfers the origin behind CloudFront and the selection can't be delivered
        // ("Unable to post message to https://docs.google.com"). window.location.origin is
        // https://app.aideyapp.com in prod and http://localhost:3000 in dev — correct in both.
        .setOrigin(window.location.origin)
        .setOAuthToken(accessToken)
        .addView(view)
        .setCallback((data) => {
          const action = data[google.picker.Response.ACTION];
          if (action === google.picker.Action.PICKED) {
            const doc = (data[google.picker.Response.DOCUMENTS] || [])[0] || {};
            resolve({ fileId: doc[google.picker.Document.ID], name: doc[google.picker.Document.NAME] || '' });
          } else if (action === google.picker.Action.CANCEL) {
            resolve(null); // user cancelled, not an error, the caller degrades quietly
          }
        });
      // Block 178 commit 4: restore the Picker developer key. Commit 1e removed it to test whether a
      // wrong key caused the 403, but that test was inconclusive — the token also lacked drive.file
      // then, so a scope 403 would have masked a key 403. Scope, setOrigin and COOP are now cleared,
      // so the missing key is the last ranked candidate for the docs.google.com/picker 403.
      if (API_KEY) builder.setDeveloperKey(API_KEY);
      builder.build().setVisible(true);
    } catch (err) {
      reject(err);
    }
  });
}

// ── Cache invalidation on connection change (Block 178 commit 3) ─────────────
// cachedToken lives for the whole page session. Connecting Google is a full-page redirect that
// reloads the SPA (so it clears this on its own), but DISCONNECTING is an in-page DELETE with no
// reload — so without this, the module would keep serving a now-revoked token that 403s opaquely at
// export. Settings disconnect handlers dispatch 'aidey:googleConnectionChanged'; we clear on it.
export function clearDriveFileToken() { cachedToken = null; }
if (typeof window !== 'undefined') {
  window.addEventListener('aidey:googleConnectionChanged', clearDriveFileToken);
}

// ── Public entry point ───────────────────────────────────────────────────────
// Ensures token + Picker, opens it (pre-navigated when fileIds given), and resolves
// { fileId, name, accessToken } or null if the user cancels. The accessToken is the
// drive.file token the caller passes to the backend export.
export async function pickGoogleDoc({ fileIds } = {}) {
  // Token FIRST: the pop-up is the gesture-sensitive step, so nothing async runs before it (GIS is
  // preloaded on open, so getDriveFileToken takes its synchronous path). The Picker library is
  // normally already warm too; awaiting it here is after the pop-up has been granted.
  const accessToken = await getDriveFileToken();
  await ensurePickerLoaded();
  const picked = await openPicker({ accessToken, fileIds });
  return picked ? { ...picked, accessToken } : null;
}

// Call when the import modal / transcripts panel OPENS so the click handler has nothing to load.
// Fire-and-forget: any load error surfaces later, at click time, through pickGoogleDoc.
export function preloadDrivePicker() {
  ensurePickerLoaded().catch(() => {});
  ensureGisLoaded().catch(() => {});
}

// True only for the pop-up-blocked failure, so callers show the "allow pop-ups" copy for that case
// alone and generic copy for everything else (timeout, unauthorised origin, dismissed consent).
export function isPopupBlocked(err) {
  return err?.code === 'popup_failed_to_open';
}

// True when even a forced re-consent didn't grant drive.file — the user actively declined the Drive
// permission. Callers show a specific, truthful message for this (distinct from a generic failure).
export function isDriveScopeDenied(err) {
  return err?.code === 'drive_scope_denied';
}

// Parse a Google Docs/Drive file id out of a pasted URL (used by import's "have a URL"
// path to pre-navigate the Picker). Returns null if the string isn't a recognisable link.
export function fileIdFromUrl(url) {
  if (!url) return null;
  const m = String(url).match(/\/d\/([a-zA-Z0-9_-]{10,})|[?&]id=([a-zA-Z0-9_-]{10,})/);
  return m ? (m[1] || m[2]) : null;
}

export const isDrivePickerConfigured = () => !!CLIENT_ID;
